import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';

// https://www.gatsbyjs.com/docs/add-seo-component/

const Head = ({ title = null, description = null, image = null }) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            defaultTitle: title
            defaultDescription: description
            siteUrl
            defaultImage: image
            twitterUsername
          }
        }
      }
    `,
  );

  const { defaultTitle, defaultDescription, siteUrl, defaultImage, twitterUsername } =
    site.siteMetadata;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image || defaultImage}`,
    url: `${siteUrl}${pathname}`,
  };

  return (
    <Helmet title={title} defaultTitle={seo.title} titleTemplate={`%s | ${defaultTitle}`}>
      <html lang="en" />

      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta
        name="keywords"
        content="产品经理,高级产品经理,Web3产品经理,区块链产品经理,加密货币支付,金融科技创新,数字化转型,产品策略,用户体验设计,移动出行解决方案,充电生态系统,OTC交易平台,加密支付网关,区块链技术,DeFi产品,产品管理顾问,科技产品领导者,创业产品经理,敏捷产品开发,product manager,senior product manager,web3 product manager,blockchain product manager,cryptocurrency payments,fintech innovation,digital transformation,product strategy,user experience design,mobility solutions,charging ecosystem,OTC trading platform,crypto payment gateway,blockchain technology,DeFi products,product management consultant,tech product leader,startup product manager,agile product development,GameFi,NFT,decentralized applications,payment systems,cross-border payments,mobile payments,automotive technology,ride-sharing platforms,fleet management,supply chain finance,risk management,compliance management,KYC/AML,data analytics,business intelligence,growth hacking,user acquisition,product operations,project management,team leadership,stakeholder management,market research,competitive analysis,product design,prototype development,A/B testing,performance optimization,system architecture,API design,database design,cloud computing,microservices,DevOps,quality assurance,security management,privacy protection,GDPR compliance,internationalization,localization,multi-language support,innovation management,business model innovation,platform economics,ecosystem development,partnership management,vendor management,budget management,resource planning,timeline management,risk assessment,change management,process improvement"
      />
      <meta name="author" content="Y.Liu" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Y.Liu Portfolio" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={twitterUsername} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:site" content={twitterUsername} />

      {/* LinkedIn */}
      <meta property="article:author" content="Y.Liu" />
      <meta property="article:section" content="Technology" />
      <meta property="article:tag" content="Product Management, Web3, Blockchain" />

      {/* Google AdSense */}
      <meta name="google-adsense-account" content="ca-pub-7555677595492839" />

      {/* Google Analytics */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=G-E5FNDW6YH7" />
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-E5FNDW6YH7');
        `}
      </script>

      {/* 钱包扩展冲突防护 CSS */}
      <style>
        {`
          /* 隐藏钱包扩展可能注入的错误提示 */
          .wallet-extension-error,
          .extension-error-overlay,
          .phantom-error,
          .metamask-error,
          .solana-error {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
          }
          
          /* 隐藏可能的钱包扩展注入的元素 */
          [data-extension-id],
          [data-wallet-extension],
          iframe[src*="extension://"],
          iframe[src*="chrome-extension://"] {
            display: none !important;
          }
          
          /* 隐藏Google Ads错误 */
          .adsbygoogle-noablate {
            display: none !important;
          }
          
          /* 防止广告脚本错误影响布局 */
          ins.adsbygoogle[data-ad-status="unfilled"] {
            display: none !important;
          }
        `}
      </style>

      {/* 钱包扩展冲突防护脚本 */}
      <script>
        {`
          // 早期防护：在页面加载前就开始防护
          (function() {
            if (typeof window === 'undefined') return;
            
            // 静默处理控制台错误
            const originalConsoleError = console.error;
            console.error = function(...args) {
              const message = args.join(' ');
              if (
                message.includes('Cannot assign to read only property') ||
                message.includes('Cannot redefine property') ||
                message.includes('inpage.js') ||
                message.includes('evmAsk.js') ||
                message.includes('extension')
              ) {
                // 静默处理钱包扩展错误
                return;
              }
              originalConsoleError.apply(console, args);
            };
            
            // 防护常见的钱包属性
            const walletProps = ['ethereum', 'solana', 'phantom', 'web3', 'tronWeb'];
            walletProps.forEach(prop => {
              try {
                if (!window.hasOwnProperty(prop)) {
                  let value;
                  Object.defineProperty(window, prop, {
                    get: () => value,
                    set: (newValue) => { value = newValue; },
                    configurable: true,
                    enumerable: true
                  });
                }
              } catch (e) {
                // 静默处理
              }
            });
          })();
        `}
      </script>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Y.Liu',
          jobTitle: 'Senior Product Manager',
          description:
            '资深产品经理，专注Web3、区块链、去中心化应用、GameFi、NFT等前沿技术领域。拥有15年产品管理经验，精通数字货币支付系统、金融科技、DeFi项目、健康管理平台、货车维修平台、出行平台等多个垂直领域。擅长产品策略、用户体验设计、敏捷开发、数据分析、增长黑客、B端C端产品设计、跨境支付、移动支付、风控系统、合规管理等核心技能。曾任职于多家知名企业，包括合创汽车、如祺出行、青桔科技等，具备丰富的汽车科技、出行服务、金融科技行业经验。Senior Product Manager specializing in Web3, blockchain, decentralized applications, GameFi, NFT and cutting-edge technologies with 15 years of experience across fintech, automotive technology, ride-sharing platforms, healthcare platforms, logistics platforms, and payment systems.',
          url: seo.url,
          image: seo.image,
          sameAs: [
            'https://linkedin.com/in/yliu-pm',
            'https://github.com/yliu-pm',
            'https://twitter.com/yliu_pm',
            'https://www.xiaohongshu.com/user/profile/643f8136000000001400d1c2',
          ],
          knowsAbout: [
            'Product Management',
            'Web3',
            'Blockchain',
            'DeFi',
            'Cryptocurrency',
            'Fintech',
            'GameFi',
            'NFT',
            'User Experience Design',
            'Product Strategy',
            'Agile Development',
            'Digital Payments',
            'Payment Systems',
            'Cryptocurrency Payments',
            'Cross-border Payments',
            'Mobile Payments',
            'B2B Products',
            'B2C Products',
            'SaaS Platforms',
            'Healthcare Platforms',
            'Logistics Platforms',
            'Automotive Technology',
            'Ride-sharing Platforms',
            'Fleet Management',
            'Supply Chain Finance',
            'Risk Management',
            'Compliance Management',
            'KYC/AML',
            'Data Analytics',
            'Business Intelligence',
            'Growth Hacking',
            'User Acquisition',
            'Product Operations',
            'Project Management',
            'Team Leadership',
            'Stakeholder Management',
            'Market Research',
            'Competitive Analysis',
            'Product Design',
            'Prototype Development',
            'A/B Testing',
            'Performance Optimization',
            'System Architecture',
            'API Design',
            'Database Design',
            'Cloud Computing',
            'Microservices',
            'DevOps',
            'Quality Assurance',
            'Security Management',
            'Privacy Protection',
            'GDPR Compliance',
            'Internationalization',
            'Localization',
            'Multi-language Support',
            'Digital Transformation',
            'Innovation Management',
            'Business Model Innovation',
            'Platform Economics',
            'Ecosystem Development',
            'Partnership Management',
            'Vendor Management',
            'Budget Management',
            'Resource Planning',
            'Timeline Management',
            'Risk Assessment',
            'Change Management',
            'Process Improvement',
            'Operational Excellence',
            'Customer Success',
            'User Research',
            'Customer Journey Mapping',
            'Service Design',
            'Digital Marketing',
            'Content Strategy',
            'Social Media Strategy',
            'SEO/SEM',
            'Conversion Optimization',
            'Retention Strategy',
            'Monetization Strategy',
            'Pricing Strategy',
            'Go-to-Market Strategy',
            'Product Launch',
            'Feature Rollout',
            'Beta Testing',
            'User Feedback Analysis',
            'Product Metrics',
            'KPI Management',
            'OKR Framework',
            'Agile Methodologies',
            'Scrum',
            'Kanban',
            'Lean Startup',
            'Design Thinking',
            'Human-Centered Design',
            'Accessibility Design',
            'Inclusive Design',
            'Sustainable Development',
            'ESG Principles',
            'Carbon Neutrality',
            'Green Finance',
            'Inclusive Finance',
            'Digital Inclusion',
            'Financial Inclusion',
            'Regulatory Technology',
            'Compliance Technology',
            'Identity Verification',
            'Biometric Authentication',
            'Multi-factor Authentication',
            'Single Sign-On',
            'OAuth',
            'JWT',
            'API Gateway',
            'Container Technology',
            'Cloud Native',
            'Edge Computing',
            'IoT',
            '5G Technology',
            'Metaverse',
            'Virtual Reality',
            'Augmented Reality',
            'Mixed Reality',
            'Digital Twin',
            'Smart Manufacturing',
            'Industry 4.0',
            'Smart Cities',
            'Smart Transportation',
            'Smart Healthcare',
            'Smart Education',
            'Smart Agriculture',
            'Smart Energy',
            'Smart Environment',
            'Artificial Intelligence',
            'Machine Learning',
            'Deep Learning',
            'Natural Language Processing',
            'Computer Vision',
            'Speech Recognition',
            'Chatbots',
            'Intelligent Customer Service',
            'Knowledge Graphs',
            'Expert Systems',
            'Decision Support Systems',
            'Data Mining',
            'Data Warehousing',
            'Data Lakes',
            'Data Platforms',
            'Business Platforms',
            'Technology Platforms',
            'Organization Platforms',
            'Digital Operations',
            'Lean Entrepreneurship',
            'User Research',
            'Market Research',
            'Competitive Intelligence',
            'Business Analysis',
            'Requirements Analysis',
            'Functional Design',
            'Wireframing',
            'Prototyping',
            'Flow Charts',
            'Use Cases',
            'Sequence Diagrams',
            'Architecture Diagrams',
            'Deployment Diagrams',
            'Monitoring Dashboards',
            'Report Design',
            'Data Visualization',
            'Statistical Analysis',
            'Gray Release',
            'Blue-Green Deployment',
            'Canary Release',
            'Continuous Integration',
            'Continuous Deployment',
            'Continuous Delivery',
            'Version Control',
            'Code Review',
            'Unit Testing',
            'Integration Testing',
            'System Testing',
            'Performance Testing',
            'Load Testing',
            'Security Testing',
            'Compatibility Testing',
            'Usability Testing',
            'User Acceptance Testing',
            'Regression Testing',
            'Test Automation',
            'Test-Driven Development',
            'Behavior-Driven Development',
            'Domain-Driven Design',
            'Event-Driven Architecture',
            'Message Queues',
            'Distributed Systems',
            'Load Balancing',
            'Caching Systems',
            'Database Optimization',
            'Index Optimization',
            'Query Optimization',
            'Storage Optimization',
            'Network Optimization',
            'CDN',
            'DNS',
            'HTTPS',
            'SSL/TLS',
            'Encryption Algorithms',
            'Hash Algorithms',
            'Digital Signatures',
            'Digital Certificates',
            'PKI',
            'Certificate Authority',
            'Key Management',
            'Access Control',
            'Permission Management',
            'Role Management',
            'Audit Logs',
            'Compliance Auditing',
            'Internal Control',
            'Process Management',
            'System Building',
            'Training Systems',
            'Knowledge Management',
            'Document Management',
            'Project Management',
            'Agile Management',
            'DevOps',
            'SRE',
            'ITIL',
            'COBIT',
            'ISO27001',
            'SOX',
            'PCI-DSS',
            'Cybersecurity',
            'Information Security',
            'Data Protection',
            'Privacy Compliance',
            'Self-controllable Technology',
            'Domestic Substitution',
            'Digital Sovereignty',
            'Data Sovereignty',
            'Algorithm Sovereignty',
            'Technology Sovereignty',
            'Network Sovereignty',
            'Digital Economy',
            'Platform Economy',
            'Sharing Economy',
            'Circular Economy',
            'Innovation Economy',
            'Knowledge Economy',
            'Service Economy',
            'Experience Economy',
            'Attention Economy',
            'Traffic Economy',
            'Fan Economy',
            'Influencer Economy',
            'Live Streaming Economy',
            'Short Video Economy',
            'Gaming Economy',
            'Virtual Economy',
            'Digital Asset Economy',
            'Token Economy',
            'DAO',
            'SocialFi',
            'Metaverse Economy',
            'Web3 Economy',
            'Decentralized Economy',
            'Distributed Economy',
            'Peer-to-Peer Economy',
            'Disintermediated Economy',
            'Trust Economy',
            'Reputation Economy',
            'Incentive Economy',
            'Governance Economy',
            'Community Economy',
            'Ecosystem Economy',
            'Open Source Economy',
            'Collaborative Economy',
            'Crowdsourcing Economy',
            'Crowdfunding Economy',
            'Maker Economy',
            'Gig Economy',
            'Freelance Economy',
            'Remote Work',
            'Hybrid Work',
            'Digital Nomad',
            'Lifelong Learning',
            'Skill Development',
            'Career Development',
            'Talent Management',
            'Organizational Development',
            'Corporate Culture',
            'Values',
            'Mission Vision',
            'Strategic Planning',
            'Business Model Innovation',
            'Product Innovation',
            'Technology Innovation',
            'Service Innovation',
            'Management Innovation',
            'Marketing Innovation',
            'Channel Innovation',
            'Supply Chain Innovation',
            'Ecosystem Innovation',
            'Platform Innovation',
            'Model Innovation',
            'Process Innovation',
            'Organizational Innovation',
            'Cultural Innovation',
            'Institutional Innovation',
            'Mechanism Innovation',
            'Incentive Innovation',
            'Evaluation Innovation',
            'Training Innovation',
            'Recruitment Innovation',
            'Performance Innovation',
            'Compensation Innovation',
            'Benefits Innovation',
            'Office Innovation',
            'Communication Innovation',
            'Collaboration Innovation',
            'Decision Innovation',
            'Execution Innovation',
            'Supervision Innovation',
            'Feedback Innovation',
            'Improvement Innovation',
            'Optimization Innovation',
            'Upgrade Innovation',
            'Transformation Innovation',
            'Change Innovation',
            'Breakthrough Innovation',
            'Disruptive Innovation',
            'Leading Innovation',
            'Driving Innovation',
            'Empowering Innovation',
            'Ecosystem Innovation',
            'Open Innovation',
            'Collaborative Innovation',
            'Win-Win Innovation',
            'Sustainable Innovation',
            'Inclusive Innovation',
            'Universal Innovation',
            'Green Innovation',
            'Intelligent Innovation',
            'Digital Innovation',
            'Network Innovation',
            'Mobile Innovation',
            'Cloud Innovation',
            'Edge Innovation',
            'Terminal Innovation',
            'Application Innovation',
            'Scenario Innovation',
            'Experience Innovation',
            'Value Innovation',
            'Efficiency Innovation',
            'Quality Innovation',
            'Cost Innovation',
            'Speed Innovation',
            'Scale Innovation',
            'Precision Innovation',
            'Security Innovation',
            'Stability Innovation',
            'Reliability Innovation',
            'Usability Innovation',
            'Aesthetic Innovation',
            'Personalization Innovation',
            'Customization Innovation',
            'Standardization Innovation',
            'Normalization Innovation',
            'Process Innovation',
            'Tool Innovation',
            'Method Innovation',
            'Theory Innovation',
            'Practice Innovation',
            'Application Innovation',
            'Promotion Innovation',
            'Popularization Innovation',
            'Education Innovation',
            'Training Innovation',
            'Learning Innovation',
            'Growth Innovation',
            'Development Innovation',
            'Progress Innovation',
            'Enhancement Innovation',
            'Optimization Innovation',
            'Perfection Innovation',
            'Excellence Innovation',
            'Leading Innovation',
            'Cutting-edge Innovation',
            'High-end Innovation',
            'Top-tier Innovation',
            'World-class Innovation',
            'International Innovation',
            'Global Innovation',
            'Localization Innovation',
            'Regional Innovation',
            'Personalization Innovation',
            'Differentiation Innovation',
            'Specialization Innovation',
            'Professional Innovation',
            'Refinement Innovation',
            'Standardization Innovation',
            'Scaling Innovation',
            'Industrialization Innovation',
            'Commercialization Innovation',
            'Marketization Innovation',
            'Socialization Innovation',
            'Public Welfare Innovation',
            'Sustainability Innovation',
          ],
          worksFor: {
            '@type': 'Organization',
            name: 'Web3 & Fintech Industry',
          },
        })}
      </script>
    </Helmet>
  );
};

export default Head;

Head.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
};
